import React, { useEffect, useState, ChangeEvent, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import debounce from 'lodash.debounce'

import { Box, Grid, TextField, Skeleton } from '@mui/material'

import { PostContent } from '@3m5/sm_publish-frontend-api'

import { getGeneralPostingOptions, getPostingConfiguration, getTheme } from '../../../store/common/common.selectors'
import {
  GeneralPostingOptions,
  PostingTemplate,
  PostTypeEnum, PreviewFormat,
  SocialMediaNetwork,
} from '@professocial/professocialTypes/types/commonTypes'
import LinkPostPreview from './LinkPostPreview'
import {
  getUrlInformationInitiated, individualizeTextInitiated,
  setUploadTemporaryMediaFile, setUrlInformation,
} from '../../../store/posting/posting.actions'
import MediaUploadPreview from '@professocial/professocialFormComponents/FormComponents/MediaUploadPreview'
import { getIndividualizedText, getUrlInformation } from '../../../store/posting/posting.selector'
import DropDownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { MediaFileMetadata, NetworkType, WebResourceMetaData } from '@3m5/sm_publish-frontend-api'
import { IndividualizeTextOperationRequest } from '@3m5/sm_publish-frontend-api'
import { PostingConfiguration } from '@professocial/professocialTypes/types/configurationTypes'
import { GetUrlInformationRequest } from '@3m5/sm_publish-frontend-api'

// Memoized wrapper to avoid re-renders of the image/video preview when parent typing occurs
const MediaUploadPreviewMemo = React.memo(function MediaUploadPreviewMemo(props: any) { return <MediaUploadPreview {...props} /> })
MediaUploadPreviewMemo.displayName = 'MediaUploadPreviewMemo'

interface IProps {
  posting: PostingTemplate;
  changePosting?: (post: PostingTemplate) => void;
  allowedSocialMediaNetworks?: SocialMediaNetwork;
  previewFormat?: PreviewFormat;
}

enum ActionType {
  None = 'None',
  Book = 'Book',
  Order = 'Order',
  Shop = 'Shop',
  LearnMore = 'LearnMore',
  SignUp = 'SignUp',
  Call = 'Call',
  Unspecified = 'Unspecified'
}

const CreatePosting: React.FC<IProps> = (props) => {
  const { t } = useTranslation()
  const theme = useSelector(getTheme)
  const dispatch = useDispatch()

  const generalPostingOptions: GeneralPostingOptions = useSelector(getGeneralPostingOptions)
  const postingConfiguration: PostingConfiguration | undefined = useSelector(getPostingConfiguration)
  const individualizedText: string | undefined = useSelector(getIndividualizedText)

  const [individualizeElement, setIndividualizeElement] = useState<'text' | 'link' | undefined>()
  const [posting, setPosting] = useState<PostingTemplate>(props.posting)
  const [postingText, setPostingText] = useState<string>(posting.content.text || '')
  const [googleCTALink, setGoogleCTALink] = useState<string>(posting.content.extensions.googleMyBusinessExtension?.ctaLink || '')
  const [linkDataLoading, setLinkDataLoading] = useState(false)
  const [ctaActionType, setCtaActionType] = useState<ActionType | undefined>()
  const lastDetectedUrlRef = useRef<string>('')
  const linkParseTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const postingRef = useRef<PostingTemplate>(posting)
  const previousUrlInformationRef = useRef<WebResourceMetaData | undefined>(undefined)

  // keep ref in sync with latest posting to allow stable callbacks without re-creating on typing
  useEffect(() => { postingRef.current = posting }, [posting])
  // keep changePosting in a ref to keep deleteImage stable regardless of parent function identity changes
  const changePostingRef = useRef(props.changePosting)
  useEffect(() => { changePostingRef.current = props.changePosting }, [props.changePosting])

  const dispatchSetTemporaryMediaFile = useCallback((params: MediaFileMetadata | undefined) => dispatch(setUploadTemporaryMediaFile(params)), [dispatch])
  const dispatchIndividualizeText = useCallback((params: IndividualizeTextOperationRequest) => dispatch(individualizeTextInitiated(params)), [dispatch])
  const dispatchGetUrlInformation = useCallback((params: { data: GetUrlInformationRequest, id?: string }) => dispatch(getUrlInformationInitiated(params)), [dispatch])
  const dispatchSetUrlInformation = useCallback((params: WebResourceMetaData | undefined) => dispatch(setUrlInformation(params)), [dispatch])
  const urlInformation: WebResourceMetaData | undefined = useSelector(getUrlInformation)

  // Sync props.posting with local state
  useEffect(() => {
    setPosting(props.posting)
    const newText = props.posting.content.text || ''
    if (newText !== postingText) {
      setPostingText(newText)
    }
  }, [props.posting])

  // Handle individualized text response
  useEffect(() => {
    if (individualizedText && individualizeElement) {
      if (individualizeElement === 'text') {
        setPostingText(individualizedText)
        const content = {
          ...posting.content,
          text: individualizedText,
        }
        const post = {
          ...posting,
          content,
        }
        props.changePosting && props.changePosting(post)
      }
      if (individualizeElement === 'link') {
        setGoogleCTALink(individualizedText)
        const content = {
          ...posting.content,
          extensions: {
            googleMyBusinessExtension: {
              ...posting.content.extensions.googleMyBusinessExtension,
              ctaActionType: ctaActionType,
              ctaLink: individualizedText,
            },
          },
        }
        const post = {
          ...posting,
          content,
        }
        props.changePosting && props.changePosting(post)
      }
      setIndividualizeElement(undefined)
    }
  }, [individualizedText, individualizeElement])

  // Handle URL information response - only when urlInformation actually changes
  useEffect(() => {
    // Only process if urlInformation has actually changed
    if (urlInformation && urlInformation !== previousUrlInformationRef.current) {
      previousUrlInformationRef.current = urlInformation

      if (urlInformation.url) {
        console.log('URL information received:', urlInformation)

        // Only set link if no image or video is attached
        if (!postingRef.current.content.image && !postingRef.current.content.video) {
          const newContent = {
            ...postingRef.current.content,
            link: {
              url: urlInformation.url,
              caption: urlInformation.title,
              description: urlInformation.description,
              imageUrl: urlInformation.metaImage?.url,
            },
          }

          const post = {
            ...postingRef.current,
            content: newContent,
          }

          if (changePostingRef.current) {
            changePostingRef.current(post)
          }
          console.log('Posting updated with link:', newContent.link)
        } else {
          console.log('Link not added because image or video is already attached')
        }

        setLinkDataLoading(false)
      }
    }
  }, [urlInformation])

  // Function to fetch URL information
  const fetchUrlInformation = useCallback((url: string) => {
    // Ensure the URL has a protocol
    const fullUrl = url.startsWith('http') ? url : `https://${url}`

    console.log('Fetching URL information for:', fullUrl)
    setLinkDataLoading(true)
    dispatchGetUrlInformation({ data: { url: fullUrl } })
  }, [dispatchGetUrlInformation])

  // Function to check and parse links from text
  const checkForLinks = useCallback((text: string) => {
    // Don't parse links if image or video is attached
    if (posting.content.image || posting.content.video) {
      console.log('Link parsing skipped: media already attached')
      return
    }

    // Don't parse if link parsing is disabled
    if (!generalPostingOptions.allowLinkParsing) {
      return
    }

    // Extract URLs from text
    const urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g
    const matches = text.match(urlRegex)

    if (matches && matches.length > 0) {
      const detectedUrl = matches[0]

      // Only process if this is a new URL
      if (detectedUrl !== lastDetectedUrlRef.current) {
        console.log('New URL detected:', detectedUrl)
        lastDetectedUrlRef.current = detectedUrl
        fetchUrlInformation(detectedUrl)
      }
    } else {
      // Clear the last detected URL if no URL is found
      lastDetectedUrlRef.current = ''
    }
  }, [posting.content.image, posting.content.video, generalPostingOptions.allowLinkParsing, fetchUrlInformation])

  // Function to individualize text
  const individualizeText = useCallback(() => {
    setIndividualizeElement('text')
    let requestSend = false

    if (props.allowedSocialMediaNetworks?.facebook && !requestSend) {
      const params: IndividualizeTextOperationRequest = {
        individualizeTextRequest: {
          networkType: NetworkType.Facebook,
          networkPageId: postingConfiguration?.networkPage?.facebookPage?.networkPageId || '',
          text: postingText,
        },
      }
      dispatchIndividualizeText(params)
      requestSend = true
    }

    if (props.allowedSocialMediaNetworks?.instagram && !requestSend) {
      const params: IndividualizeTextOperationRequest = {
        individualizeTextRequest: {
          networkType: NetworkType.Instagram,
          networkPageId: postingConfiguration?.networkPage?.instagramPage?.networkPageId || '',
          text: postingText,
        },
      }
      dispatchIndividualizeText(params)
      requestSend = true
    }

    if (props.allowedSocialMediaNetworks?.googleMyBusiness && !requestSend) {
      const params: IndividualizeTextOperationRequest = {
        individualizeTextRequest: {
          networkType: NetworkType.GoogleMyBusiness,
          networkPageId: postingConfiguration?.networkPage?.googleMyBusinessPage?.networkPageId || '',
          text: postingText,
        },
      }
      dispatchIndividualizeText(params)
    }
  }, [
    props.allowedSocialMediaNetworks,
    postingConfiguration,
    postingText,
    dispatchIndividualizeText
  ])

  // Main text change handler - NO DEBOUNCING for smooth input
  const changeMessage = useCallback((event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = event.target.value

    // Update local state immediately for smooth typing
    setPostingText(newValue)

    // Update parent state immediately
    const content = {
      ...posting.content,
      text: newValue,
    }
    const post = {
      ...posting,
      content,
    }
    props.changePosting && props.changePosting(post)

    // Clear any existing link parse timeout
    if (linkParseTimeoutRef.current) {
      clearTimeout(linkParseTimeoutRef.current)
    }

    // Set up new timeout for link parsing (2 seconds after user stops typing)
    if (generalPostingOptions.allowLinkParsing || generalPostingOptions.allowIndividualizeUserLinks) {
      linkParseTimeoutRef.current = setTimeout(() => {
        checkForLinks(newValue)

        // Check for individualization if enabled and URL is found
        if (generalPostingOptions.allowIndividualizeUserLinks) {
          const urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g
          if (newValue.match(urlRegex)) {
            individualizeText()
          }
        }
      }, 2000)
    }
  }, [posting, props.changePosting, generalPostingOptions, checkForLinks, individualizeText])

  // Debounced CTA link check
  const debouncedCheckCTALink = useMemo(() =>
    debounce((link: string) => {
      const urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g
      const detectURL = link.match(urlRegex)

      if (detectURL && generalPostingOptions.allowIndividualizeUserLinks) {
        setIndividualizeElement('link')
        const params: IndividualizeTextOperationRequest = {
          individualizeTextRequest: {
            networkType: NetworkType.GoogleMyBusiness,
            networkPageId: postingConfiguration?.networkPage?.googleMyBusinessPage?.networkPageId || '',
            text: link,
          },
        }
        dispatchIndividualizeText(params)
      }
    }, 1500),
  [
    generalPostingOptions.allowIndividualizeUserLinks,
    postingConfiguration?.networkPage?.googleMyBusinessPage?.networkPageId,
    dispatchIndividualizeText
  ]
  )

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (linkParseTimeoutRef.current) {
        clearTimeout(linkParseTimeoutRef.current)
      }
      debouncedCheckCTALink.cancel()
    }
  }, [debouncedCheckCTALink])

  const changeLink = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = event.target.value
    setGoogleCTALink(newValue)

    const content = {
      ...posting.content,
      extensions: {
        googleMyBusinessExtension: {
          ...posting.content.extensions.googleMyBusinessExtension,
          ctaActionType: ctaActionType,
          ctaLink: newValue,
        },
      },
    }
    const post = {
      ...posting,
      content,
    }
    props.changePosting && props.changePosting(post)

    if (generalPostingOptions.allowIndividualizeUserLinks) {
      debouncedCheckCTALink(newValue)
    }
  }

  const getPostType = (content: PostContent) => {
    let newPostType = PostTypeEnum.MESSAGE

    if (content.link) {
      newPostType = PostTypeEnum.LINK
    }
    if (content.image) {
      newPostType = PostTypeEnum.PHOTO
    }
    if (content.video) {
      newPostType = PostTypeEnum.VIDEO
    }
    return newPostType
  }

  useEffect(() => {
    const content = {
      ...posting.content,
      extensions: {
        googleMyBusinessExtension: {
          ...posting.content.extensions.googleMyBusinessExtension,
          ctaActionType: ctaActionType,
        },
      },
    }

    const post = {
      ...posting,
      content,
    }
    props.changePosting && props.changePosting(post)
  }, [ctaActionType])

  const changeButton = (event: DropDownElement) => {
    if (event === null) {
      setCtaActionType(ActionType.None)
      return
    }

    switch (event.key) {
    case 'Unspecified':
      setCtaActionType(ActionType.Unspecified)
      break
    case 'Book':
      setCtaActionType(ActionType.Book)
      break
    case 'Order':
      setCtaActionType(ActionType.Order)
      break
    case 'Shop':
      setCtaActionType(ActionType.Shop)
      break
    case 'LearnMore':
      setCtaActionType(ActionType.LearnMore)
      break
    case 'Call':
      setCtaActionType(ActionType.Call)
      break
    case 'SignUp':
      setCtaActionType(ActionType.SignUp)
      break
    case 'None':
    default:
      setCtaActionType(ActionType.None)
      break
    }
  }

  const deleteImage = useCallback(() => {
    const latestPosting = postingRef.current
    const content = {
      headline: latestPosting.content.headline,
      text: latestPosting.content.text,
      extensions: latestPosting.content.extensions,
    }
    dispatchSetUrlInformation(undefined)
    dispatchSetTemporaryMediaFile(undefined)
    const post = {
      ...latestPosting,
      content,
    }
    if (changePostingRef.current) {
      changePostingRef.current(post)
    }
  }, [dispatchSetUrlInformation, dispatchSetTemporaryMediaFile])

  const generateOptions = (): DropDownElement[] => {
    return Object.entries(ActionType).map(([key, value]) => {
      return { key: key, value: t(`postingCommon:actionButton${value}`) }
    })
  }

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Box style={{ marginBottom: theme.spacing(2.5) }}>
            {generalPostingOptions.maxCharacters
              ? (
                <TextField
                  variant="standard"
                  label={t('postingCommon:enterMessage')}
                  value={postingText}
                  helperText={
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                      <Box>{postingText.length}/{generalPostingOptions.maxCharacters}</Box>
                      {postingText.length >= generalPostingOptions.maxCharacters &&
                        <Box>{t('postingCommon:errorMessageTooLong')}</Box>}
                    </Box>
                  }
                  error={postingText.length >= generalPostingOptions.maxCharacters}
                  onChange={changeMessage}
                  minRows={5}
                  fullWidth
                  multiline
                  required
                />
              )
              : (
                <TextField
                  variant="outlined"
                  label={t('postingCommon:description')}
                  value={postingText}
                  onChange={changeMessage}
                  minRows={5}
                  fullWidth
                  multiline
                  required
                />
              )}
          </Box>
          {props.allowedSocialMediaNetworks?.googleMyBusiness && posting.socialMediaNetwork.googleMyBusiness &&
            <>
              <Box style={{ marginBottom: theme.spacing(2.5) }}>
                <DropDownComponent
                  disableClearable
                  options={generateOptions()}
                  label={t('postingCommon:addButton')}
                  onChange={changeButton}
                  value={posting.content.extensions.googleMyBusinessExtension?.ctaActionType}
                  variant="standard"
                />
              </Box>
              <Box style={{ marginBottom: theme.spacing(2.5) }}>
                <TextField
                  label={t('postingCommon:addLink')}
                  disabled={!ctaActionType}
                  variant="standard"
                  defaultValue={posting.content.link && posting.content.link.url}
                  onChange={changeLink}
                  size="small"
                  value={googleCTALink}
                  fullWidth
                />
              </Box>
            </>}
        </Grid>
        {getPostType(posting.content) === PostTypeEnum.PHOTO && (posting.content.image) && !linkDataLoading &&
          <Grid item xs={12}>
            <MediaUploadPreviewMemo
              previewFormat={props.previewFormat || PreviewFormat.square}
              view="row"
              deleteMediaFile={deleteImage}
              src={posting.content.image.url}
              showAsPopUp={generalPostingOptions.showAsPopUp}
            />
          </Grid>}
        {getPostType(posting.content) === PostTypeEnum.VIDEO && (posting.content.video) && !linkDataLoading &&
          <Grid item xs={12}>
            <MediaUploadPreviewMemo
              previewFormat={props.previewFormat || PreviewFormat.square}
              view="row"
              deleteMediaFile={deleteImage}
              src={posting.content.video.url}
              showAsPopUp={generalPostingOptions.showAsPopUp}
              type="video"
            />
          </Grid>}
        {!linkDataLoading
          ? (
            <>
              {getPostType(posting.content) === PostTypeEnum.LINK &&
                <LinkPostPreview posting={posting} deleteImage={deleteImage}/>}
            </>
          ) : (
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={3}>
                <Skeleton variant="rectangular" width="100%" height={152}/>
              </Grid>
              <Grid item container spacing={2} xs={9}>
                {[...Array(3)].map((x, i) => (
                  <Grid key={i} item xs={12}>
                    <Skeleton variant="rectangular" width="100%" height={40}/>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
      </Grid>
    </>
  )
}

export default CreatePosting
